<template>
  <div class="page-default page-report">
    <!-- The KJP tab lays this row out as a column (tab strip above, panel
         filling the rest); the Generate Report tab keeps the default row
         direction, because its two sections sit side by side. Hence the
         modifier rather than a blanket change to .page-section. -->
    <div class="row m-0 page-section bg-dashboard-dark-2"
      :class="{ 'page-section-kjp': activeTab === 'kjp' }">
      <div class="col-12 px-3 pt-2 pb-0">
        <div class="kjp-tabbar d-flex align-items-end">
          <a href="#" class="kjp-tab" :class="{ active: activeTab === 'report' }"
             @click.prevent="activeTab = 'report'">Generate Report</a>
          <a href="#" class="kjp-tab ml-1" :class="{ active: activeTab === 'kjp' }"
             @click.prevent="activeTab = 'kjp'">Contract Ceiling</a>
        </div>
      </div>
      <template v-if="activeTab === 'report'">
      <div class="col col-md-4 col-lg-3 section-left" style="flex: 0 0 300px; max-width: 300px"
        :class="{ show: showSidebar }">
        <div class="case-container border-outline-dark bg-dashboard-dark-1">
          <div class="case-header text-capitalize text-white">
            <div class="case-title text-truncate">Generate Report</div>
            <div class="case-header-icon clickable d-block d-md-none" @click="toggleSidebar()">
              <font-awesome-icon :icon="['fas', 'times']" />
            </div>
          </div>
          <div class="case-body py-3 px-4">
            <div class="form-group">
              <label for="selectDistrict" class="mb-1 text-light">District</label>
              <div class="input-group input-group-sm">
                <select id="selectDistrict" class="custom-select bg-dark-input text-white" v-model="models.district"
                  data-toggle="tooltip" data-placement="bottom" title="Select district" @mouseover="helper.showTooltip"
                  @mouseleave="helper.hideTooltip">
                  <option :value="null" disabled>-- Select district --</option>
                  <option v-for="district in districts" :key="district._id" :value="district._id">
                    {{ district.name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label for="selectDateFrom" class="mb-1 text-light">
                Report Type
              </label>
              <div class="input-group input-group-sm">
                <select id="selectType" class="custom-select bg-dark-input text-white" v-model="models.type"
                  data-toggle="tooltip" data-placement="bottom" title="Select report type"
                  @mouseover="helper.showTooltip" @mouseleave="helper.hideTooltip">
                  <option :value="null" disabled>
                    -- Select report type --
                  </option>
                  <option value="maintenance:R01">R01</option>
                  <option value="maintenance:R02">R02</option>
                  <option value="maintenance:R06">R06</option>
                  <option value="inspection">R09</option>
                  <option value="KKE">KKE</option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label for="selectDateFrom" class="mb-1 text-light">Source</label>
              <div class="input-group input-group-sm">
                <select id="selectSource" class="custom-select bg-dark-input text-white" v-model="models.source"
                  data-toggle="tooltip" data-placement="bottom" title="Select case source"
                  @mouseover="helper.showTooltip" @mouseleave="helper.hideTooltip">
                  <option :value="null" disabled>
                    -- Select Case Source --
                  </option>
                  <option value="All">All</option>
                  <option value="mobile">Mobile</option>
                  <option value="dashcam">Dashcam</option>
                  <option value="drone">Drone</option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label for="selectDateFrom" class="mb-1 text-light">Time</label>
              <div class="input-group input-group-sm">
                <select id="selectTime" class="custom-select bg-dark-input text-white" v-model="models.time"
                  data-toggle="tooltip" data-placement="bottom" title="Select time" @mouseover="helper.showTooltip"
                  @mouseleave="helper.hideTooltip">
                  <option :value="null" disabled>-- Select Time --</option>
                  <option value="All">All</option>
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label for="selectDateFrom" class="mb-1 text-light">Date Range</label>
              <v-date-picker is-dark class="inline-calendar" is-range v-model="models.date" mode="date" trim-weeks />
            </div>
            <div class="text-right">
              <button class="btn btn-primary btn-sm px-4" @click="generateReport" :disabled="disableApplyButton"
                data-toggle="tooltip" data-placement="bottom" title="Click to Apply" @mouseover="helper.showTooltip"
                @mouseleave="helper.hideTooltip">
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="col section-right text-truncate d-flex px-0 px-md-3">
        <div class="case-container bg-dashboard-dark-1 w-100 border-outline-dark">
          <div class="section-header w-100 bg-dashboard-dark-1 border-outline-dark-bottom"
            :class="{ 'shadow-none': cases.length < 1 }">
            <button class="btn btn-outline-primary shadow-sm mr-3 section-left-toggler" @click="toggleSidebar()"
              data-toggle="tooltip" data-placement="bottom" title="Click to open sidebar"
              @mouseover="helper.showTooltip" @mouseleave="helper.hideTooltip">
              <font-awesome-icon :icon="['fas', 'angle-right']" />
            </button>
            <p v-if="isInitial" class="font-weight-normal small mb-0 font-italic flex-grow-1 text-center text-muted">
              Select date to generate report
            </p>
            <div class="d-flex justify-content-between flex-grow-1" v-else-if="cases.length > 0">
              <div class="form-inline flex-grow-1">
                <div class="input-group flex-grow-1">
                  <div class="input-group-prepend">
                    <span class="input-group-text px-3 bg-dashboard-dark-2 border-0">
                      <font-awesome-icon :icon="['fas', 'file-invoice']" />
                    </span>
                  </div>
                  <input ref="reportNameInput" type="text"
                    class="form-control form-control-default form-control-sm mb-0 border-0 flex-grow-1 font-weight-bold"
                    placeholder="Enter report name here and hit enter" v-model="models.title"
                    @keyup.enter="changeReportTitle" @blur="changeReportTitle" />
                </div>
              </div>
              <button class="btn btn-sm px-3 btn-primary rounded-pill btn-download" @click="downloadReport()"
                :disabled="!readyToPrint" data-toggle="tooltip" data-placement="bottom" title="Click to download report"
                @mouseover="helper.showTooltip" @mouseleave="helper.hideTooltip">
                <font-awesome-icon :icon="['fas', 'file-download']" />
              </button>
            </div>
          </div>
          <div id="reportBody" class="report-body border-outline-dark-top" v-if="!isInitial">
            <div class="report-item p-2 w-100 h-100 bg-darker" v-if="reportType == 'inspection' || cases.length > 0">
              <report-container :reportType="reportType" :cases="cases" :title="reportTitle"
                :district="selectedDistrict" :date-range="models.date" :user-role="userRole"
                @reportLoaded="changePrintState" @processingDone="(...args) => processingDone(...args)" />
            </div>
            <div class="font-weight-normal small mb-0 font-italic flex-grow-1 text-center text-muted mt-4" v-else>
              No report available on selected dates
            </div>
          </div>
        </div>
      </div>
      </template>
      <template v-if="activeTab === 'kjp'">
        <kjp-summary class="kjp-panel" :user-role="userRole" :user-districts="userDistricts" />
      </template>
    </div>
    <spinner v-if="isProcessing" :message="loadingMessage" />
  </div>
</template>

<script>
import Moment from "moment";
import Helper from "@/helper";
import API from "@/api";
import Spinner from "@/components/Spinner";
import ReportContainer from "@/components/ReportContainer";
import KjpSummary from "@/components/kjp/KjpSummary";

export default {
  name: "reports",
  components: {
    Spinner,
    ReportContainer,
    KjpSummary,
  },
  data() {
    return {
      // Seeded from the URL so a reload (or a pasted link) lands on the tab
      // the user was actually on. Anything other than "kjp" falls back to
      // "report", so a hand-edited or stale query can't leave the page on a
      // tab that does not exist.
      activeTab: this.$route.query.tab === "kjp" ? "kjp" : "report",
      isInitial: true,
      isProcessing: false,
      showSidebar: false,
      readyToPrint: false,
      cases: [],
      districts: [],
      reportTitle: null,
      reportType: null,
      models: {
        title: null,
        type: "inspection",
        source: "All",
        district: null,
        time: "All",
        date: {
          start: null,
          end: null,
        },
      },
      selectedDistrict: "",
      userDistricts: [],
      userRole: null,
      loadingMessage: null,
      helper: Helper,
    };
  },
  watch: {
    // Mirror the active tab into the URL, so Ctrl-R comes back to it rather
    // than resetting to Generate Report (and so the KJP tab can be linked to).
    //
    // `replace`, not `push`: switching tabs is a change of view, not a
    // navigation step the back button should have to walk back through one
    // click at a time. The default "report" tab clears the parameter instead
    // of writing ?tab=report, to keep the plain /#/reports URL canonical.
    //
    // The catch swallows vue-router's NavigationDuplicated rejection, which
    // it throws when the target URL already matches — harmless here, but an
    // unhandled rejection in the console otherwise.
    activeTab(tab) {
      const query = { ...this.$route.query };

      if (tab === "report") delete query.tab;
      else query.tab = tab;

      this.$router.replace({ query }).catch(() => {});
    },
  },
  computed: {
    disableApplyButton() {
      if (
        !this.models.type ||
        !this.models.district ||
        !this.models.date.start ||
        !this.models.date.end
      ) {
        return true;
      }

      return false;
    },
    reportDistrict() {
      const district = this.districts.find(
        (district) => district._id == this.models.district
      );

      return district ? district : {};
    },
  },
  methods: {
    processingDone(...args) {
      this.isProcessing = [...args][0];

      this.loadingMessage = [...args][1];
    },
    changePrintState(val) {
      this.readyToPrint = val;
    },
    changeReportTitle() {
      this.reportTitle = this.models.title;

      this.$refs.reportNameInput.blur();
    },
    downloadReport() {
      this.$nextTick(() => {
        document.title = this.buildReportFilename();
        window.print();
        window.onafterprint = (e) => {
          document.title = "Infrasel";
        };
      });
    },
    buildReportFilename() {
      if (this.reportTitle) {
        return `Inspection Report ${this.reportTitle}`;
      }

      const reportTypeLabels = {
        inspection: "R09",
        KKE: "KKE",
        "maintenance:R01": "R01",
        "maintenance:R02": "R02",
        "maintenance:R06": "R06",
      };

      const parts = ["Inspection Report"];

      const daerah = this.reportDistrict && this.reportDistrict.name;
      if (daerah) parts.push(daerah);

      const typeLabel = reportTypeLabels[this.reportType] || this.reportType;
      if (typeLabel) parts.push(typeLabel);

      if (this.models.source && this.models.source !== "All") {
        parts.push(
          this.models.source.charAt(0).toUpperCase() +
            this.models.source.slice(1)
        );
      }

      if (this.models.time && this.models.time !== "All") {
        parts.push(this.models.time);
      }

      if (this.models.date.start && this.models.date.end) {
        const s = Moment(this.models.date.start).format("YYYYMMDD");
        const e = Moment(this.models.date.end).format("YYYYMMDD");
        parts.push(`${s} to ${e}`);
      }

      return parts.join(" ");
    },
    toggleSidebar() {
      this.showSidebar = !this.showSidebar;
    },
    async generateReport() {
      this.isInitial = true;
      this.isProcessing = true;
      this.showSidebar = false;

      this.reportTitle = null;
      this.models.title = null;

      let startDate = Moment(this.models.date.start).startOf("day").utc();
      let endDate = Moment(this.models.date.end).endOf("day").utc();

      let dateType = ["inspection", "KKE"].includes(this.models.type)
        ? "timestamp"
        : "solveDate";

      startDate = ["inspection", "KKE"].includes(this.models.type)
        ? startDate.toISOString(true)
        : startDate.unix();
      endDate = ["inspection", "KKE"].includes(this.models.type)
        ? endDate.toISOString(true)
        : endDate.unix();

      this.reportType = this.models.type;

      let sourceQuery =
        this.models.source && this.models.source == "mobile"
          ? "&type=mobile"
          : this.models.source && this.models.source == "dashcam"
            ? "&type=ai&caption_contains=Dashcam"
            : this.models.source && this.models.source == "drone"
              ? "&type=ai&caption_contains=drone"
              : null;

      let url = `cases?_limit=-1&_sort=createdAt&district=${this.models.district}&${dateType}_gte=${startDate}&${dateType}_lte=${endDate}`;

      if (sourceQuery) {
        url += sourceQuery;
      }

      if (this.models.time) {
        url +=
          this.models.time == "AM"
            ? `&createdTime_null=false&createdTime_gte=23:00:00.000&createdTime_gte=00:00:00.000&createdTime_lte=10:59:59.999`
            : this.models.time == "PM"
              ? "&createdTime_null=false&createdTime_gte=11:00:00.000&createdTime_lte=22:59:59.999"
              : "";
      }

      if (this.reportType == "inspection") {
        url +=
          "&reportType_in=R01&reportType_in=R02&reportType_in=R03&reportType_in=R04&reportType_in=R05&reportType_in=R06&reportType_in=R07&reportType_in=R08&reportType_in=R09";
        // url += "&reportType_in=R09";

        // if (this.models.district == "6042786166d30058184d135b") {
          url += "&reportType_in=KKE";
        // }
      } else if (
        this.reportType.includes("maintenance:") ||
        this.reportType == "KKE"
      ) {
        const type = this.reportType.replace("maintenance:", "");

        url += `&reportType_in=${type}`;
      }

      API.get(url)
        .then((retVal) => {
          let _cases = retVal.data.map((c) => {
            const formattedTimestamp = Helper.formatDate(c.timestamp);

            if (formattedTimestamp) {
              c.caseTimeSegment =
                formattedTimestamp.split(" ")[
                formattedTimestamp.split(" ").length - 1
                ];

              return c;
            }
          });

          this.cases = _cases;

          this.selectedDistrict = this.reportDistrict;

          this.isInitial = false;
          this.isProcessing = false;
        })
        .catch((err) => {
          console.error(err);

          this.isProcessing = false;
        });
    },
    async getDistricts() {
      this.isProcessing = true;

      let url = "districts?_sort=name";

      if (this.userRole == "Supervisor" && this.userDistricts.length > 0) {
        url = url
          .concat("&_id_in=")
          .concat(
            this.userDistricts.map((district) => district._id).join("&_id_in=")
          );
      }

      API.get(url)
        .then((retVal) => {
          this.districts = retVal.data;

          if (this.districts && this.districts.length == 1) {
            this.models.district = this.districts[0]._id;
          }

          this.isProcessing = false;
        })
        .catch((err) => {
          console.error(err);

          this.isProcessing = false;
        });
    },
  },
  mounted() {
    let _user = localStorage.getItem("user");

    if (_user) {
      _user = JSON.parse(_user);

      if (_user.districts && _user.districts.length > 0) {
        this.userDistricts = _user.districts.map((district) => {
          return {
            _id: district._id,
            name: district.name,
          };
        });
      }

      this.userRole = _user.role.name;
    }

    this.getDistricts();
  },
};
</script>

<style lang="scss" scoped>
// Viewport fix for the KJP tab.
//
// .page-section is `height: 100%; overflow: hidden` with 17px of vertical
// padding, and its children are a ~53px tab strip and the KJP panel. The panel
// carried `h-100`, which claims 100% of the section's content box — the full
// height, as though the tab strip above it were not there. Section content and
// panel therefore totalled ~53px more than the box, and `overflow: hidden`
// clipped the excess: the panel's own scroll container ended below the visible
// area, so scrolling it to the bottom still left the last table rows cut off.
//
// Sizing the panel by flex instead of by percentage is what fixes it — it now
// asks for whatever the tab strip left behind rather than for everything.
// `min-height: 0` is required because a flex item's default `min-height: auto`
// refuses to shrink below its content, which would reintroduce the overflow
// the moment the table got tall.
//
// Scoped to the KJP tab: Generate Report's two sections sit side by side and
// need the row's default direction.
.page-section-kjp {
  flex-direction: column;
  flex-wrap: nowrap;
}

.page-section-kjp > .col-12 {
  flex: 0 0 auto;
}

// Vue 2 applies a parent's scope id to a child component's root element, which
// is what lets this reach KjpSummary's own outermost div.
.page-section-kjp > .kjp-panel {
  flex: 1 1 auto;
  min-height: 0;
}

// Page-level folder tabs (INFS-1570, Hanif feedback 19 Oct): same shape as
// the R01/R02/R06 tabs in KjpSummary.vue, but in one accent rather than the
// per-scope colours, so the two tab rows do not clash. The accent is the one
// Hanif picked for the CMS Budget subtabs. Contrast on the #1b1b1b section:
// white on the dimmed fill (accent at 0.6 alpha) is 5.9:1, #111 on the full
// accent is 6.4:1; white on the full accent would be 2.9, hence the dark ink.
$page-tab-color: #4c9be8;
$page-tab-dimmed: rgba(76, 155, 232, 0.6);

.kjp-tabbar {
  // The active tab overlaps this edge (margin-bottom: -3px below), so tab and
  // page read as one piece of card.
  border-bottom: 3px solid $page-tab-color;
}

.kjp-tab {
  position: relative;
  color: #fff;
  background-color: $page-tab-dimmed;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.3rem 1rem;
  border-radius: 8px 8px 0 0;
  // Both tabs the same width whatever the label, as on the scope tabs.
  flex: 0 1 11rem;
  min-width: 0;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  // Inactive tab sits a little lower; margin rather than transform, so the
  // strip's height does not change when the tab changes.
  margin-top: 0.2rem;

  // Underline rather than brighten: a brighter fill would drop white text
  // below 4.5:1.
  &:hover {
    color: #fff;
    text-decoration: underline;
  }

  &.active {
    color: #111;
    background-color: $page-tab-color;
    margin-top: 0;
    padding-top: 0.5rem;
    margin-bottom: -3px;
    padding-bottom: calc(0.3rem + 3px);
    text-decoration: none;
  }
}
</style>